import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitBranch } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  PageHeader, Spinner, EmptyState, ScoreBadge, PlatformTag, STAGE_LABELS, PIPELINE_STAGES,
} from "@/components/app/widgets";

export default function Pipeline() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/prospects").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const move = async (prospectId, stage) => {
    try {
      await api.post(`/prospects/${prospectId}/stage`, { stage });
      toast.success(`Moved to ${STAGE_LABELS[stage]}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!items) return <Spinner label="Loading pipeline" />;

  return (
    <div data-testid="pipeline-page">
      <PageHeader title="Partnership Pipeline" sub="Every relationship, from discovered to revenue-generating." />
      {items.length === 0 ? (
        <EmptyState icon={GitBranch} title="Pipeline is empty" desc="Run discovery from a campaign to start building relationships." />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-6">
          {PIPELINE_STAGES.map((stage) => {
            const inStage = items.filter((p) => p.status === stage);
            return (
              <div key={stage} data-testid={`pipeline-col-${stage.toLowerCase()}`} className="w-64 shrink-0">
                <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                    {STAGE_LABELS[stage]}
                  </span>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-bold text-zinc-400">
                    {inStage.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {inStage.map((p) => (
                    <div key={p.prospect_id} data-testid={`pipeline-card-${p.prospect_id}`} className="border border-[#27272A] bg-[#0A0A0A] p-4 transition-colors hover:border-[#D4AF37]/40">
                      <Link to={`/app/prospects/${p.prospect_id}`} className="block truncate text-sm font-medium text-white hover:text-[#D4AF37]">
                        {p.name}
                      </Link>
                      <div className="mt-2 flex items-center justify-between">
                        <PlatformTag platform={p.platform} />
                        <ScoreBadge score={p.score?.total} />
                      </div>
                      <select
                        value={stage}
                        onChange={(e) => move(p.prospect_id, e.target.value)}
                        data-testid={`move-${p.prospect_id}`}
                        className="mt-3 w-full rounded-md border border-[#27272A] bg-[#050505] px-2 py-1.5 text-[11px] text-zinc-400 outline-none focus:border-[#D4AF37]"
                      >
                        {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
